/**
 * Google Analytics 4 (gtag.js) helpers.
 *
 * The tag itself is injected from the root route head. Page views are sent
 * manually on router navigation (send_page_view is disabled) so that the SPA
 * reports one hit per route change instead of only the first document load.
 *
 * Every hit carries a `display_mode` parameter so installed-app (PWA) traffic
 * can be separated from ordinary browser visits in reports.
 */

export const GA_MEASUREMENT_ID = "G-WQS6YZTNSM";

type GtagArgs = [command: string, ...rest: unknown[]];

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: GtagArgs) => void;
  }
}

/** standalone = launched from the installed app icon, browser = normal tab. */
export function displayMode(): "standalone" | "twa" | "minimal-ui" | "browser" {
  if (typeof window === "undefined") return "browser";
  const nav = window.navigator as unknown as { standalone?: boolean };
  if (document.referrer.startsWith("android-app://")) return "twa";
  if (window.matchMedia?.("(display-mode: standalone)").matches) return "standalone";
  if (window.matchMedia?.("(display-mode: minimal-ui)").matches) return "minimal-ui";
  if (nav.standalone === true) return "standalone";
  return "browser";
}

export function isInstalledApp(): boolean {
  return displayMode() !== "browser";
}

function gtag(...args: GtagArgs) {
  if (typeof window === "undefined") return;
  // The inline snippet defines window.gtag; fall back to dataLayer if the
  // script is still loading so no early event is dropped.
  if (typeof window.gtag === "function") {
    window.gtag(...args);
    return;
  }
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(args);
}

/** Common parameters attached to every event. */
function baseParams(): Record<string, unknown> {
  const mode = displayMode();
  return {
    display_mode: mode,
    platform: mode === "browser" ? "web" : "pwa",
    app_installed: mode !== "browser",
    site_language:
      typeof document !== "undefined" && document.documentElement.lang === "en" ? "en" : "ar",
  };
}

/** Track a custom event. Names must be snake_case for GA4. */
export function track(name: string, params: Record<string, unknown> = {}) {
  gtag("event", name, { ...baseParams(), ...params });
}

/** Manual page_view, called on every router navigation. */
export function trackPageView(path: string, title?: string) {
  if (typeof window === "undefined") return;
  gtag("set", "user_properties", {
    display_mode: displayMode(),
    app_installed: String(isInstalledApp()),
  });
  gtag("event", "page_view", {
    ...baseParams(),
    page_path: path,
    page_location: window.location.href,
    page_title: title ?? document.title,
    page_referrer: document.referrer || undefined,
  });
}
